import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Trash2, XCircle } from 'lucide-react';

interface TaskActionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onDrop: () => void;
  onDelete: () => void;
}

/**
 * TaskActionsSheet - 行動版底部滑入式操作選單
 *
 * 設計理念：
 * - iOS 風格的底部 Action Sheet
 * - 提供 Complete、Drop 兩個次要操作
 * - 使用 spring 動畫創造流暢的滑入/滑出體驗
 */
export const TaskActionsSheet: React.FC<TaskActionsSheetProps> = ({
  isOpen,
  onClose,
  onComplete,
  onDrop,
  onDelete,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 overlay-light z-modal-nested"
            onClick={onClose}
          />

          {/* Sheet 容器 */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            className="fixed inset-x-0 bottom-0 z-modal-nested"
          >
            <div className="bg-white rounded-t-3xl shadow-2xl">
              {/* 拖曳指示器 */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* 操作選項列表 */}
              <div className="px-4 pb-4">
                {/* Complete 按鈕 */}
                <button
                  onClick={() => {
                    onComplete();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-green-50 active:bg-green-100 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-full">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-green-600">Complete</div>
                    <div className="text-sm text-green-500">Mark as done</div>
                  </div>
                </button>

                {/* 分隔線 */}
                <div className="my-2 border-t border-gray-100" />

                {/* Drop 按鈕（需確認操作） */}
                <button
                  onClick={() => {
                    onClose(); // 先關閉 Sheet
                    onDrop(); // 觸發確認對話框
                  }}
                  className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-orange-50 active:bg-orange-100 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-full">
                    <XCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-orange-600">Drop</div>
                    <div className="text-sm text-orange-400">Mark as dropped</div>
                  </div>
                </button>

                {/* 分隔線 */}
                <div className="my-2 border-t border-gray-100" />

                {/* Delete 按鈕（永久刪除，紅色） */}
                <button
                  onClick={() => {
                    onClose(); // 先關閉 Sheet
                    onDelete(); // 觸發確認對話框
                  }}
                  className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-red-50 active:bg-red-100 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-red-600">Delete</div>
                    <div className="text-sm text-red-400">Permanently remove</div>
                  </div>
                </button>
              </div>

              {/* Safe Area 底部間距 */}
              <div className="h-safe-bottom" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
